/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ButtonLabelLayout } from '../UNSAFE_ButtonLabelLayout';

import { MenuButtonDropdownIcon } from '../PRIVATE_ThemedIcons/MenuButtonDropdownIcon';

import { Menu } from '../UNSAFE_Menu';
import { useMenuAction, MenuToggleDetail } from '../hooks/UNSAFE_useMenuAction';
import { useTooltip } from '../hooks/UNSAFE_useTooltip';

import { BaseButton } from '../UNSAFE_BaseButton';

import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';

import { ComponentChildren, ComponentChild, Fragment } from 'preact';
import { forwardRef, ForwardedRef, ElementRef } from 'preact/compat';
import { useRef, useImperativeHandle } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { TestIdProps } from '../hooks/UNSAFE_useTestId';

type ButtonHandle = ElementRef<typeof BaseButton>;

type WidthInterpolationProps = Pick<DimensionProps, 'width'>;

export type BaseProps = WidthInterpolationProps & {
  /**
   * children - One or more MenuItems, dividers, or Menu groups
   */
  children?: ComponentChildren;

  /**
   * The icon is the button's icon.  Note that this is not the menu
   * expand icon shown on the right (or to the left in right to left mode).
   */
  icon?: ComponentChild;

  /**
   * Specifies that the button element should be disabled.
   */
  isDisabled?: boolean;

  /**
   * Specifies if only the icon is shown, and no down chevron.
   */
  isIconOnly?: boolean;

  /**
   * Specifes the button type
   */
  type?: 'button' | 'submit';

  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the button.
   */
  'aria-describedby'?: string;

  /**
   * Specifies if the menu is open
   */
  isMenuOpen?: boolean;

  /**
   * Indicates in what states the button has chrome (background and border).
   *     "ghost": "Ghost buttons are the least prominent variant. Ghost buttons are
   * useful for performing low-priority tasks, such as manipulating the UI.",
   *     "borderless"': "Borderless buttons are a more prominent variant. Borderless
   * buttons are useful for supplemental actions that require minimal emphasis.",
   *     "outlined"': "Outlined buttons are salient, but lighter weight than
   * solid buttons. Outlined buttons are useful for secondary actions.",
   *     "solid"': "Solid buttons stand out, and direct the user's attention to the
   *  most important actions in the UI.",
   */
  variant?: 'ghost' | 'borderless' | 'outlined' | 'solid';

  /**
   * The size indicates how large the button is rendered.
   */
  size?: 'xs' | 'sm' | 'md' | 'lg';

  /**
   * Property that triggers a callback immediately when toggle happens and value of expanded property should be updated
   */
  onToggleMenu?: (details: MenuToggleDetail) => void;
};

/**
 * Note that either an accessibleLabel or tooltip must be specified.
 */
type VariationAccessibleLabelProps =
  | {
      /**
       * accessibleLabel - the aria label
       */
      accessibleLabel: string;

      /**
       * tooltip - overrides the accessibleLabel for the tooltip
       */
      tooltip?: string;
    }
  | {
      accessibleLabel?: string;

      tooltip: string;
    };

/**
 * A IconMenuButton is a combined action and menu button.
 */
export const IconMenuButton = forwardRef(
  (
    {
      children,
      tooltip,
      accessibleLabel,
      variant = 'outlined',
      isDisabled = false,
      isMenuOpen = false,
      isIconOnly = false,
      onToggleMenu,
      size = 'md',
      type: buttonType = 'button',
      testId,
      width,
      icon,
      'aria-describedby': ariaDescribedBy
    }: BaseProps & VariationAccessibleLabelProps & TestIdProps,
    ref: ForwardedRef<ButtonHandle>
  ) => {
    const buttonRef = useRef<ButtonHandle>(null);
    useImperativeHandle(ref, () => buttonRef.current!, [buttonRef]);

    const { triggerProps, menuProps } = useMenuAction({
      isDisabled: isDisabled,
      onToggleMenu: onToggleMenu,
      isMenuOpen: isMenuOpen,
      anchorRef: buttonRef
    });

    const { tooltipContent, tooltipProps } = useTooltip({
      text: tooltip ? tooltip : accessibleLabel,
      isDisabled: isMenuOpen || isDisabled
    });

    const elementDetails = {
      type: 'button' as const,
      buttonType: buttonType
    };

    const ariaProps = { 'aria-describedby': ariaDescribedBy };

    const rootButton = (
      <Fragment>
        <BaseButton
          {...mergeProps(tooltipProps, triggerProps, ariaProps)}
          styling={isMenuOpen ? ['min', 'active'] : ['min']}
          elementDetails={elementDetails}
          isDisabled={isDisabled}
          variant={variant}
          width={width}
          size={size}
          ref={buttonRef}
          aria-label={accessibleLabel ?? tooltip}
          testId={testId}>
          <ButtonLabelLayout
            display={'icons'}
            startIcon={icon}
            endIcon={isIconOnly ? null : <MenuButtonDropdownIcon />}
            size={size}></ButtonLabelLayout>
          {tooltipContent}
        </BaseButton>
      </Fragment>
    );

    /*TODO: Remove this hardcoded offsetValue so we have dropdown offset only set on one single place.
     Could be done via menu props or having a dropdownMenu component. JET-60595*/
    return isDisabled ? (
      rootButton
    ) : (
      <Fragment>
        {rootButton}
        <Menu {...menuProps} offsetValue={4} aria-label={accessibleLabel ?? tooltip}>
          {children}
        </Menu>
      </Fragment>
    );
  }
);

IconMenuButton.displayName = 'IconMenuButton';
